'use client';

import { useEffect } from 'react';

import { HiSparkles } from 'react-icons/hi';
import { useCalendarState } from '@/app/calendar/utils/useCalendarState';
import { useCoachChatStore } from '@/app/stores/coachChatStore';
import { trpc } from '@/app/api/trpc/client';

export interface CoachButtonProps {
  /**
   * Id of the CoachMessage (chat bubble) to scroll to once chat opens
   * Omit for the default "just open chat" behavior (e.g. the
   * global nav button). Missing/stale ids are a no-op in ChatWindow, never
   * an error.
   */
  messageId?: string | null;
  /** Defaults to "Coach". */
  label?: string;
  className?: string;
}

export function CoachButton({
  messageId,
  label = 'Coach',
  className,
}: CoachButtonProps = {}) {
  const { showChatWindow, openChatAtMessage } = useCalendarState();
  const { unreadCount, setUnreadCount } = useCoachChatStore();
  const { mutate: markRead } = trpc.coachChat.markRead.useMutation({
    onSuccess: () => setUnreadCount(0),
  });

  // Reset unread when chat opens
  useEffect(() => {
    if (showChatWindow) {
      setUnreadCount(0);
      markRead(undefined, {
        onError: (e) => console.error('Failed to mark read', e),
      });
    }
  }, [showChatWindow, markRead, setUnreadCount]);

  return (
    <button
      className={className ?? 'btn btn-secondary rounded-full py-2 relative'}
      onClick={() => openChatAtMessage(messageId)}
      aria-label='Coach'
    >
      <HiSparkles size={15} className='text-yellow-300' />
      <span className='hidden xs:inline uppercase text-yellow-300 lg:text-sm'>
        {label}
      </span>
      {unreadCount > 0 && (
        <span className='absolute top-0 right-0 -mt-1 -mr-1 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full'>
          {unreadCount}
        </span>
      )}
    </button>
  );
}
